import { useCallback, useState } from "react";
import { getCashMovementsByDateRange } from "../firebase/cashMovement/getCashMovementsByDateRange";
import { getPaymentsByDateRange } from "../firebase/payment/getPaymentsByDateRange";
import { getReturnsByDateRange } from "../firebase/returns/getReturnsByDateRange";
import { getInvoiceByNumber } from "../firebase/invoice/getInvoiceByNumber";
import { CashMovementRecord } from "../models/cashMovement";
import {
	calculateShopCashSummary,
	ShopCashSummary
} from "../utils/shopCashCalculations";

const emptySummary: ShopCashSummary = {
	cashReceived: 0,
	grossCashReceived: 0,
	upiReceived: 0,
	cashRefunded: 0,
	partnerWithdrawals: 0,
	partnerCashWithdrawals: 0,
	partnerUpiWithdrawals: 0,
	shopExpenses: 0,
	shopCashExpenses: 0,
	shopUpiExpenses: 0,
	availableCash: 0,
	availableUpi: 0
};

export const useShopCash = () => {
	const [cashMovements, setCashMovements] = useState<CashMovementRecord[]>(
		[]
	);
	const [summary, setSummary] = useState<ShopCashSummary>(emptySummary);
	const [loading, setLoading] = useState(true);

	const fetchShopCashData = useCallback(
		async (startDate: string, endDate: string) => {
			setLoading(true);
			try {
				const [movements, paymentsInRange, returnsInRange] =
					await Promise.all([
						getCashMovementsByDateRange(startDate, endDate),
						getPaymentsByDateRange(startDate, endDate),
						getReturnsByDateRange(startDate, endDate)
					]);
				const invoiceNumbers = Array.from(
					new Set(
						returnsInRange.map(
							(returnedItem) => returnedItem.invoiceNumber
						)
					)
				);
				const invoiceResults = await Promise.all(
					invoiceNumbers.map((invoiceNumber) =>
						getInvoiceByNumber(invoiceNumber)
					)
				);
				const invoiceTotalsByInvoiceNumber = invoiceResults.reduce(
					(acc: Record<string, number>, invoice, index) => {
						const invoiceNumber = invoiceNumbers[index];
						if (invoice) {
							acc[invoiceNumber] = invoice.items.reduce(
								(total, item) =>
									total + item.sellPrice * item.quantity,
								0
							);
						}
						return acc;
					},
					{}
				);

				const sortedMovements = movements.sort(
					(a, b) =>
						new Date(b.movementDate).getTime() -
						new Date(a.movementDate).getTime()
				);

				setCashMovements(sortedMovements);
				setSummary(
					calculateShopCashSummary({
						paymentsInRange,
						returnsInRange,
						cashMovements: sortedMovements,
						invoiceTotalsByInvoiceNumber
					})
				);
			} catch (err) {
				console.error("Failed to load shop cash data:", err);
			} finally {
				setLoading(false);
			}
		},
		[]
	);

	return { cashMovements, summary, loading, fetchShopCashData };
};
